import { Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AbilityService,
  DropdownData,
  GenericDropdownComponent,
  LookupsService,
  NgbModalService,
  rxResourceFixed,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { map, of } from 'rxjs';
import { DocGeneration } from '../doc-generation/doc-generation';
import { AmendmentDocument } from '../amendment-document/amendment-document';
import { SalesApsService } from '../../../../services/sales-aps.service';
import { BuyerContract } from '../../../../models/buyer-contract.model';
import { miscellaneousDocument, ApsDealDocument } from '../../../../models/deal-document.models';
import { DealDocumentHelper } from '../../../../helpers/deal-document.helper';
import { CustomDocument } from '../custom-document/custom-document';
import { SignableDocumentCard } from '../signable-document-card/signable-document-card';
import { DealDocumentsService } from 'libs/sales/src/lib/services/deal-documents.service';

export type MiscDocumentCreateType = 'coop' | 'amendment' | 'custom';

@Component({
  selector: 'lib-miscellaneous-docs',
  imports: [
    DocGeneration,
    AmendmentDocument,
    CustomDocument,
    SignableDocumentCard,
    SvgIconsComponent,
    GenericDropdownComponent
  ],
  templateUrl: './miscellaneous-docs.component.html',
  styleUrl: './miscellaneous-docs.component.scss',
  standalone: true
})
export class MiscellaneousDocsComponent {
  readonly #salesApsService = inject(SalesApsService);
  readonly #lookupsService = inject(LookupsService);
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #dealDocService = inject(DealDocumentsService);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  generateDocumentModal = viewChild<TemplateRef<unknown>>('generateDocumentModal');

  effectiveDeal = computed(() => this.#salesApsService.currentDeal());
  effectiveDealId = computed(() => this.#salesApsService.currentDeal()?.id ?? '');
  effectiveProjectCode = computed(() => this.effectiveDeal()?.project.projectCode ?? '');

  selectedCreateType = signal<MiscDocumentCreateType>('coop');

  readonly createTypeOptions: { id: MiscDocumentCreateType; label: string }[] = [
    { id: 'coop', label: 'COOP' },
    { id: 'amendment', label: 'Amendment' },
    { id: 'custom', label: 'Custom' }
  ];

  documentsResource = rxResourceFixed<ApsDealDocument[], string>({
    params: () => this.effectiveDealId(),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params) {
        return of([]);
      }
      return this.#dealDocService.getDealNonApsDocuments(params);
    }
  });

  contractDetailsResource = rxResourceFixed<BuyerContract, { dealId: string }>({
    params: () => ({ dealId: this.effectiveDealId() ?? '' }),
    stream: ({ params }) => {
      if (!params.dealId) {
        return of({} as BuyerContract);
      }
      return this.#salesApsService
        .getBuyerContract(params.dealId)
        .pipe(map(contract => contract ?? ({} as BuyerContract)));
    },
    defaultValue: {} as BuyerContract
  });

  getProjectId = rxResourceFixed({
    params: () => this.effectiveProjectCode() ?? '',
    defaultValue: '',
    stream: ({ params }) => {
      if (!params) return of('');
      return this.#lookupsService
        .readProjects()
        .pipe(map(projects => projects.find(project => project.projCode === params)?.id ?? ''));
    }
  });

  documentTemplatesResource = rxResourceFixed<miscellaneousDocument[], { projectId: string; dealId: string }>({
    params: () => ({ projectId: this.getProjectId.value() ?? '', dealId: this.effectiveDealId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId || !params.dealId) {
        return of([]);
      }
      return this.#salesApsService.getProjectNonApsDocuments(params.projectId);
    }
  });

  contractDetails = computed(() => this.contractDetailsResource.value());
  projectId = computed(() => this.getProjectId.value() ?? '');
  /** All project non-APS templates from the API. */
  documentTemplates = computed(() => this.documentTemplatesResource.value() ?? []);
  /** Generated non-APS deal documents (always a list). */
  documents = computed(() => {
    const raw = this.documentsResource.value();
    if (raw == null) {
      return [];
    }
    return Array.isArray(raw) ? raw : [raw];
  });
  amendmentDocuments = computed(() => this.documents().filter(doc => DealDocumentHelper.isAmendmentDealDocument(doc)));
  miscDocuments = computed(() => this.documents().filter(doc => !DealDocumentHelper.isAmendmentDealDocument(doc)));
  nextAmendmentSequence = computed(() => this.amendmentDocuments().length + 1);
  templatesForGeneration = computed(() =>
    DealDocumentHelper.filterMiscellaneousTemplatesPendingGeneration(this.documentTemplates(), this.miscDocuments())
  );

  createTypeDropdownItems = computed<DropdownData[]>(() =>
    this.createTypeOptions.map(option => ({
      id: option.id,
      firstLabel: option.label
    }))
  );

  openGenerateDocumentModal(type: MiscDocumentCreateType = 'coop'): void {
    if (!this.canWriteDeals()) return;
    if (!this.effectiveDealId()) {
      return;
    }

    this.selectedCreateType.set(type);

    this.#modalService.open(this.generateDocumentModal(), {
      ...this.#ngbModalService.centerModal,
      size: 'lg'
    });
  }

  onCreateTypeChange(value: string | null | undefined): void {
    if (value === 'coop' || value === 'amendment' || value === 'custom') {
      this.selectedCreateType.set(value);
    }
  }

  onDocumentGenerated(modal: { close: (result?: string) => void }): void {
    this.reloadData();
    modal.close('generated');
  }

  reloadData(): void {
    this.documentsResource.reload();
    this.documentTemplatesResource.reload();
    this.contractDetailsResource.reload();
  }
}
